import { AuthDialog } from "@/components/AuthDialog";
import { LocationSelector } from "@/components/LocationSelector";
import { NotificationButton } from "@/components/NotificationButton";
import { useTheme } from "@/components/ThemeProvider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useTopicLocale } from "@/hooks/use-topic-locale";
import { getIdToken, signOut } from "@/lib/firebase";
import { useQuery } from "@tanstack/react-query";
import {
  Languages,
  Menu,
  MessageCircle,
  Moon,
  Settings,
  Shield,
  Sun,
  UserCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";

export function NewHeader() {
  const { user, userProfile, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { toast } = useToast();
  const { isKorean, setLocale } = useTopicLocale();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsAdmin(false);
      return;
    }

    const userEmail = user.email?.toLowerCase().trim();
    if (!userEmail) {
      setIsAdmin(false);
      return;
    }

    const adminEmailsEnv = import.meta.env.VITE_ADMIN_EMAILS || "";
    const adminEmails = adminEmailsEnv
      .split(",")
      .map((email: string) => email.trim().toLowerCase())
      .filter((email: string) => email.length > 0);

    const isClientAdmin = adminEmails.includes(userEmail);
    if (isClientAdmin) setIsAdmin(true);

    const checkAdminStatus = async () => {
      try {
        const token = await getIdToken(true);
        if (!token) return;

        const response = await fetch("/api/auth/check-admin", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = (await response.json()) as { isAdmin?: boolean };
          setIsAdmin(Boolean(data.isAdmin));
        }
      } catch (error) {
        console.error("Failed to check admin status:", error);
      }
    };

    void checkAdminStatus();
  }, [isAuthenticated, user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: isKorean ? "로그아웃" : "Signed out",
        description: isKorean
          ? "로그아웃되었습니다."
          : "You have been signed out.",
      });
      // 마이페이지/설정처럼 로그인을 요구하는 화면에 로그아웃 후 그대로 남아있지 않도록
      // 홈으로 이동시킨다 (그런 화면들은 각자 useEffect 가드가 있어 치명적이진 않았지만,
      // 가드가 없는 화면도 있어 명시적으로 리다이렉트한다).
      setLocation("/");
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  // 순서는 docs/OZK_FINAL_LAUNCH_STRATEGY.md 2장 고정: 생활문제 해결 · 뉴스 ·
  // 업체찾기 · 동네 톡톡 · 동네 마켓. 메뉴 "개편"이 아니라 순서 조정이므로
  // 항목 자체는 그대로 두고 순서만 맞춘다.
  const navigationItems = [
    {
      href: "/topics",
      match: "/topics",
      ko: "생활문제 해결",
      en: "Life Solutions",
      testId: "link-life-problems",
    },
    {
      href: "/news",
      match: "/news",
      ko: "뉴스",
      en: "News",
      testId: "link-news",
    },
    {
      href: "/business-directory",
      match: "/business-directory",
      ko: "업체찾기",
      en: "Find Services",
      testId: "link-business-directory",
    },
    {
      href: "/community-talk",
      match: "/community-talk",
      ko: "동네 톡톡",
      en: "Neighbourhood Talk",
      testId: "link-community-talk",
    },
    {
      href: "/market",
      match: "/market",
      ko: "동네 마켓",
      en: "Local Market",
      testId: "link-my-area",
      anchor: false,
    },
  ];

  const marketNavigationItems = [
    { href: "/market", ko: "동네마켓 전체", en: "All Local Market" },
    {
      href: "/market?category=community",
      ko: "중고거래",
      en: "Used goods",
    },
    { href: "/market?category=used-car", ko: "자동차", en: "Cars" },
    {
      href: "/market?category=real-estate",
      ko: "부동산",
      en: "Property",
    },
  ];

  const isActive = (path: string) => {
    if (path.includes("#")) return false;
    if (path === "/") return location === "/";
    return location.startsWith(path);
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto w-full max-w-[1536px] px-4 md:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-3">
            <Link href="/">
              <div className="cursor-pointer" data-testid="link-home">
                <img
                  src="/logo.png"
                  alt="OZK"
                  className="h-8 w-auto object-contain"
                />
              </div>
            </Link>

            <nav
              className="hidden items-center gap-0.5 xl:flex"
              aria-label={isKorean ? "주요 메뉴" : "Primary navigation"}
            >
              {navigationItems.map((item) => {
                const content = (
                  <div
                    className={`cursor-pointer whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive(item.match)
                        ? "text-primary"
                        : "text-foreground hover:bg-muted hover:text-primary"
                    }`}
                    data-testid={item.testId}
                  >
                    {isKorean ? item.ko : item.en}
                  </div>
                );

                return item.anchor ? (
                  <a key={item.href} href={item.href}>
                    {content}
                  </a>
                ) : (
                  <Link key={item.href} href={item.href}>
                    {content}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="xl:hidden"
                    aria-label={isKorean ? "전체 메뉴 열기" : "Open full menu"}
                    data-testid="button-responsive-full-menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>
                    {isKorean ? "주요 메뉴" : "Primary navigation"}
                  </DropdownMenuLabel>
                  {navigationItems.map((item) => {
                    const label = isKorean ? item.ko : item.en;
                    return (
                      <DropdownMenuItem key={item.href} asChild>
                        {item.anchor ? (
                          <a href={item.href} className="w-full">
                            {label}
                          </a>
                        ) : (
                          <Link href={item.href} className="w-full">
                            {label}
                          </Link>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>
                    {isKorean ? "동네 마켓" : "Local Market"}
                  </DropdownMenuLabel>
                  {marketNavigationItems.map((item) => (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link href={item.href} className="w-full">
                        {isKorean ? item.ko : item.en}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <LocationSelector
                variant="compact"
                className="hidden max-w-36 text-foreground hover:bg-muted md:inline-flex [&>span]:truncate"
              />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLocale(isKorean ? "en" : "ko")}
                className="gap-1.5 px-2 text-foreground hover:bg-muted"
                aria-label={
                  isKorean ? "Switch to English" : "한국어로 전환"
                }
                data-testid="button-home-locale"
              >
                <Languages className="h-4 w-4" />
                <span className="hidden text-xs font-semibold lg:inline">
                  {isKorean ? "EN" : "한국어"}
                </span>
              </Button>

              {isAuthenticated && <ChatButtonWithBadge />}
              <NotificationButton buttonClassName="text-foreground hover:bg-muted" />

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-foreground hover:bg-muted"
                data-testid="button-theme-toggle"
                aria-label={isKorean ? "화면 테마 변경" : "Change theme"}
              >
                {theme === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              {isAuthenticated && user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex cursor-pointer items-center gap-2"
                      aria-label={isKorean ? "사용자 메뉴" : "User menu"}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={userProfile?.photoURL || ""} />
                        <AvatarFallback>
                          {user.email?.charAt(0).toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setLocation("/mypage")}>
                      <UserCircle className="mr-2 h-4 w-4" />
                      {isKorean ? "마이페이지" : "My page"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setLocation("/mypage")}>
                      <Settings className="mr-2 h-4 w-4" />
                      {isKorean ? "설정" : "Settings"}
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setLocation("/admin")}
                        >
                          <Shield className="mr-2 h-4 w-4" />
                          {isKorean ? "관리자" : "Admin"}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      {isKorean ? "로그아웃" : "Sign out"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  onClick={() => setAuthDialogOpen(true)}
                  className="text-sm text-foreground transition-colors hover:text-primary"
                >
                  {isKorean ? "로그인" : "Sign in"}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
    </>
  );
}

function ChatButtonWithBadge() {
  const [, setLocation] = useLocation();

  const { data: unreadData } = useQuery<{ unread_count: number }>({
    queryKey: ["/api/chat/unread-count"],
    enabled: true,
    refetchInterval: 30000,
    queryFn: async () => {
      const token = await getIdToken(true);
      if (!token) throw new Error("Not authenticated");

      const response = await fetch("/api/chat/unread-count", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch unread count");
      return response.json();
    },
  });

  const unreadCount = unreadData?.unread_count || 0;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setLocation("/chat")}
      className="relative text-foreground hover:bg-muted"
      data-testid="button-chat"
      aria-label="Chat"
    >
      <MessageCircle className="h-5 w-5" />
      {unreadCount > 0 && (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </Button>
  );
}
